import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Newspaper, Calendar, Store } from "lucide-react";
import { fetchPublishedArticles, ARTICLE_CATEGORY_LABELS } from "@/lib/articles.functions";
import { fetchActiveDealers } from "@/lib/dealers.functions";
import { fetchUpcomingEvents, EVENT_CATEGORY_LABELS } from "@/lib/events.functions";
import { NewsletterSignup } from "@/components/newsletter-signup";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";
import { SmartImage } from "@/components/smart-image";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/community/")({
  head: () => ({
    meta: [
      { title: "Community Hub | News, Events & UK Card Dealers | BidNova Auctions" },
      {
        name: "description",
        content:
          "Latest UK trading card news, upcoming card shows and a directory of trusted dealers and hobby shops — all in the BidNova Community Hub.",
      },
      { property: "og:title", content: "Community Hub — BidNova Auctions" },
      {
        property: "og:description",
        content: "UK trading card news, card shows and dealer directory.",
      },
      { property: "og:url", content: "https://bidnovaauctions.uk/community" },
      { property: "og:type", content: "website" },
      ...socialImageMeta(),
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/community" }],
    scripts: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Community", path: "/community" }])],
  }),
  component: CommunityHome,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function CommunityHome() {
  const { t } = useTranslation("community");
  const { data: articles = [] } = useQuery({
    queryKey: ["community", "home-articles"],
    queryFn: () => fetchPublishedArticles(6),
    staleTime: 60_000,
  });
  const { data: events = [] } = useQuery({
    queryKey: ["community", "home-events"],
    queryFn: () => fetchUpcomingEvents(3),
    staleTime: 60_000,
  });
  const { data: dealers = [] } = useQuery({
    queryKey: ["community", "home-dealers"],
    queryFn: () => fetchActiveDealers(6),
    staleTime: 60_000,
  });

  return (
    <div className="mx-auto max-w-7xl px-6 sm:px-8 pt-10 pb-16 space-y-16">
      <HubTiles />
      <SectionHeader
        icon={Newspaper}
        title={t("home.articlesTitle", "Latest News & Guides")}
        to="/community/articles"
        subtitle={t("home.articlesEyebrow", "Market insights, buying guides and community stories")}
        viewAllLabel={t("home.viewAll", "View all →")}
      />
      {articles.length ? (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((a) => (
            <Link
              key={a.id}
              to="/community/articles/$slug"
              params={{ slug: a.slug }}
              className="group block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_20px_50px_-20px_rgba(139,92,246,0.4)]"
            >
              {a.cover_image_url && (
                <div className="aspect-[16/9] overflow-hidden bg-black/40">
                  <SmartImage
                    src={a.cover_image_url}
                    alt={`Cover image for ${a.title}`}
                    width={480}
                    height={270}
                    widths={[360, 480, 720]}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 380px"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              )}
              <div className="p-5">
                <p className="text-[11px] uppercase tracking-widest text-primary">
                  {ARTICLE_CATEGORY_LABELS[a.category] ?? a.category}
                </p>
                <h3 className="mt-2 font-display text-lg text-white line-clamp-2">{a.title}</h3>
                {a.excerpt && (
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{a.excerpt}</p>
                )}
                <div className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary group-hover:gap-2 transition-all">
                  {t("home.readMore", "Read more")} <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <EmptyState text={t("home.emptyArticles", "Articles are coming soon.")} />
      )}

      <SectionHeader
        icon={Calendar}
        title={t("home.eventsTitle", "Upcoming UK Card Shows")}
        to="/community/events"
        subtitle={t("home.eventsEyebrow", "Trade nights, tournaments and grading meetups")}
        viewAllLabel={t("home.viewAll", "View all →")}
      />
      {events.length ? (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((ev) => (
            <Link
              key={ev.id}
              to="/events/$slug"
              params={{ slug: ev.slug }}
              className="group block rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40"
            >
              <p className="text-[11px] uppercase tracking-widest text-primary">
                {EVENT_CATEGORY_LABELS[ev.category] ?? ev.category}
              </p>
              <h3 className="mt-2 font-display text-lg text-white line-clamp-2">{ev.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {new Date(ev.start_date + "T00:00:00").toLocaleDateString("en-GB", {
                  weekday: "short",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
              {(ev.venue || ev.city) && (
                <p className="text-sm text-muted-foreground line-clamp-1">
                  {[ev.venue, ev.city, ev.country].filter(Boolean).join(", ")}
                </p>
              )}
            </Link>
          ))}
        </div>
      ) : (
        <EmptyState text={t("home.emptyEvents", "No upcoming events yet — check back soon.")} />
      )}

      <SectionHeader
        icon={Store}
        title={t("home.dealersTitle", "Featured Dealers")}
        to="/community/dealers"
        subtitle={t("home.dealersEyebrow", "Trusted UK card shops, hobby stores and online retailers")}
        viewAllLabel={t("home.viewAll", "View all →")}
      />
      {dealers.length ? (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {dealers.map((d) => (
            <Link
              key={d.id}
              to="/community/dealers/$slug"
              params={{ slug: d.slug }}
              className="group flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40"
            >
              <div className="grid h-14 w-14 place-items-center rounded-xl border border-white/10 bg-black/40 overflow-hidden shrink-0">
                {d.logo_url ? (
                  <SmartImage src={d.logo_url} alt={`${d.name} logo`} width={56} height={56} widths={[56, 112]} className="h-full w-full object-cover" />
                ) : (
                  <Store className="h-6 w-6 text-muted-foreground" aria-hidden />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-lg text-white truncate">{d.name}</h3>
                <p className="text-sm text-muted-foreground line-clamp-1">
                  {[d.town || d.city, d.county, d.country].filter(Boolean).join(", ")}
                </p>
                {d.description && (
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{d.description}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <EmptyState text={t("home.emptyDealers", "Dealer directory is coming soon.")} />
      )}

      <NewsletterSignup />
    </div>
  );
}

function HubTiles() {
  const { t } = useTranslation("community");
  const tiles = [
    { to: "/community/articles", icon: Newspaper, title: t("home.tiles.articlesTitle", "News & Articles"), desc: t("home.tiles.articlesDesc", "Market insights & buying guides") },
    { to: "/community/events", icon: Calendar, title: t("home.tiles.eventsTitle", "UK Card Shows"), desc: t("home.tiles.eventsDesc", "Upcoming events & tournaments") },
    { to: "/community/dealers", icon: Store, title: t("home.tiles.dealersTitle", "Dealer Directory"), desc: t("home.tiles.dealersDesc", "Trusted UK card shops") },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      {tiles.map(({ to, icon: Icon, title, desc }) => (
        <Link
          key={to}
          to={to}
          className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_20px_50px_-20px_rgba(139,92,246,0.4)]"
        >
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/15 text-primary">
            <Icon className="h-5 w-5" aria-hidden />
          </div>
          <h2 className="mt-4 font-display text-xl text-white">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
          <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary group-hover:gap-2 transition-all">
            {t("home.tiles.explore", "Explore")} <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </div>
        </Link>
      ))}
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
  to,
  viewAllLabel,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  to: string;
  viewAllLabel: string;
}) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div>
        <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-primary">
          <Icon className="h-3 w-3" aria-hidden /> {subtitle}
        </p>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl text-white">{title}</h2>
      </div>
      <Link
        to={to}
        className="text-sm text-muted-foreground hover:text-primary transition-colors"
      >
        {viewAllLabel}
      </Link>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center text-muted-foreground">
      {text}
    </div>
  );
}
